import { ListSaleFormsRes } from "gen/api/company/service_pb"
import { SyncCartPricesReq_CartProduct as CartProduct } from "gen/api/sale/service_pb"
import { SaleForm_CustomField } from "gen/company/models_pb"
import { LedgerMethods } from "gen/ledger/models_pb"
import { Product } from "gen/product/models_pb"
import {
  FractionalMoney as PBFractionalMoney,
  Money as PBMoney,
} from "gen/proto/money/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import {
  DepositConfig,
  SaleProduct_Type as SaleProductType,
  TaxLine,
} from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { FractionalMoney, Money } from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"
import { isValidUomSnapshot } from "shared-ts/libs/product/unitsOfMeasure.helpers"
import { OptionType, OptionTypes } from "shared-ts/types/tender/tender.types"

import { NotesModalFormData } from "../NotesModal/modalNotes.types"
import { CellProductType } from "../SaleProductTable/row/cell.types"
import { RestockFee } from "../salesSlice"
import { isTallyProduct } from "./tally"
import { ValueTypes } from "../SaleTotalCostFields/ValueTypeInput"
import { SaleProduct } from "../types"
import {
  ChosenDepositValue,
  RestockFeeObjectType,
  SaleProductWithOrderInfo,
} from "../types/SaleForm.types"

import type { CartKit } from "../kits/kitState"

export function getProductPriceFractional(
  unitPriceFractional: PBFractionalMoney | undefined,
  unitPrice: PBMoney | undefined
): FractionalMoney {
  return unitPriceFractional
    ? FractionalMoney.fromPB(unitPriceFractional)
    : FractionalMoney.fromMoney(Money.fromPB(unitPrice))
}

export function isLineAmountValid(
  amount: FractionalMoney,
  quantity: Decimal
): boolean {
  return amount.isValid() && amount.mult(quantity).isValid()
}

export function findIdxInSaleProducts(
  saleProducts: SaleProduct[],
  id: PBUUID
): number {
  // find the index of the product in the array
  return saleProducts.findIndex((product) => {
    return UUID.eqFromPB(id, product.id)
  })
}

// buildDepositConfig packages the deposit intent for SyncCartPrices
export function buildDepositConfig(
  chosenDepositValue: ChosenDepositValue
): DepositConfig {
  if (chosenDepositValue.type === ValueTypes.DOLLARS) {
    return {
      value: { oneofKind: "flatAmount", flatAmount: chosenDepositValue.amount },
    }
  }
  return {
    value: {
      oneofKind: "percentage",
      percentage: chosenDepositValue.percentage.div(Decimal.fromBigSource(100)),
    },
  }
}

export function areCustomFieldsEqual(
  first: SaleForm_CustomField[],
  second: SaleForm_CustomField[]
): boolean {
  if (first.length !== second.length) {
    return false
  }
  return first.every(
    (cf, index) => cf.stringValue === second[index].stringValue
  )
}

export function getExistingProductWithNotesInCartIdx(
  productId: PBUUID | undefined,
  saleProductsInCart: SaleProduct[],
  {
    receiptNotes,
    internalNotes,
    tintColor,
    customTintColor,
    customFields,
  }: Pick<
    NotesModalFormData,
    | "receiptNotes"
    | "internalNotes"
    | "tintColor"
    | "customTintColor"
    | "customFields"
  >,
  type: SaleProductType,
  itemSetId?: UUID,
  itemSetIdBySaleLineId?: Record<string, string>
): number {
  return saleProductsInCart.findIndex(
    (product) =>
      UUID.eqFromPB(product.productId, productId) &&
      product.type === type &&
      product.notes === receiptNotes &&
      product.internalNotes === internalNotes &&
      UUID.eqFromPB(product.tintColorId, tintColor?.id) &&
      product.customTintColor === customTintColor &&
      areCustomFieldsEqual(product.customFields, customFields) &&
      itemSetIdBySaleLineId?.[UUID.idString(product.id)] ===
        itemSetId?.toString()
  )
}

export function sumTaxLines(taxLines: TaxLine[]): Money {
  return Money.sum(...taxLines.map((line) => Money.fromPB(line.tax)))
}

export function sumTaxesByRateId(
  taxesByRateId: Record<string, PBMoney>
): Money {
  return Money.sum(...Object.values(taxesByRateId).map(Money.fromPB))
}

export function makeCartProductsList(
  saleProducts?: SaleProduct[]
): CartProduct[] {
  if (!saleProducts || saleProducts.length === 0) {
    return []
  }

  const cartProducts: CartProduct[] = saleProducts.map((sp) => ({
    productId: sp.productId,
    id: sp.id,
    quantityV2: sp.quantityV2,
    ...(sp.uomId && isValidUomSnapshot(sp.uomSnapshot)
      ? {
          uomId: sp.uomId,
          quantityUom: sp.quantityUom,
        }
      : {}),
    isPriceEdited: sp.isPriceEdited,
    productEditedPrice: sp.isPriceEdited ? sp.price : undefined,
    productEditedPriceFractional: sp.isPriceEdited
      ? getProductPriceFractional(sp.priceFractional, sp.price)
      : undefined,
    quantityReturnedV2: sp.quantityReturnedV2,
    ...(sp.uomIdReturned && isValidUomSnapshot(sp.uomSnapshotReturned)
      ? {
          uomIdReturned: sp.uomIdReturned,
          quantityReturnedUom: sp.quantityReturnedUom,
        }
      : {}),
    isSpecialOrderableV2: sp.isSpecialOrderableV2,
    saleProductType: sp.type,
    returnedFromSaleProductId: sp.returnedFromSaleProductId,
    isTaxed: sp.isTaxed,
    isTaxedEdited: sp.isTaxedEdited,
    disabledAdditionalFeeIds: sp.additionalFees
      .filter((f) => f.isDisabled)
      .map((f) => f.feeId),
  }))
  return cartProducts
}

export const getLedgerMethodsListString = (
  ledgerMethods: LedgerMethods | undefined
) => {
  if (!ledgerMethods || !ledgerMethods.methods) {
    return ""
  }

  return [
    ...new Set(
      ledgerMethods.methods.map((lm) => {
        return lm.displayName
      })
    ),
  ]
    .sort()
    .join(", ")
}

export const getLedgerMethodEnumListString = (
  methods?: LedgerMethods
): string => {
  if (!methods || methods.methods.length === 0) {
    return ""
  }

  return [...new Set(methods.methods.map((lm) => lm.displayName))]
    .filter(Boolean)
    .sort()
    .join(", ")
}

export function extractRestockFeeValues(restockFee?: RestockFee) {
  if (!restockFee) {
    return { restockFeeAmount: undefined, restockFeePercentage: undefined }
  }

  return {
    restockFeeAmount:
      restockFee.type === RestockFeeObjectType.FIXED
        ? restockFee.amount
        : undefined,
    restockFeePercentage:
      restockFee.type === RestockFeeObjectType.RATE
        ? restockFee.rate
        : undefined,
  }
}

export function getRestockFeeDisplay(restockFee?: RestockFee): string {
  if (!restockFee) return ""

  switch (restockFee.type) {
    case "rate":
      return `(${restockFee.rate?.toString()}%)`
    case "fixed":
      return `(${restockFee.amount?.format()})`
    default:
      return ""
  }
}

export function getSubmissionLabel(amount: Money, optionType: OptionType) {
  if (
    optionType === OptionTypes.CARD ||
    optionType === OptionTypes.CHARGE_ACCOUNT ||
    optionType === OptionTypes.CUSTOM ||
    optionType === OptionTypes.ACH
  ) {
    return `Charge ${amount.format()}`
  }
  if (optionType === OptionTypes.WRITE_OFF) {
    return `Write off ${amount.format()}`
  }
  return `Accept ${amount.format()} ${getOptionName(optionType)}`
}

function getOptionName(optionType: OptionType): string {
  switch (optionType) {
    case OptionTypes.CARD:
      return "card"
    case OptionTypes.CASH:
      return "cash"
    case OptionTypes.CHECK:
      return "check"
    default:
      return "" // should not happen
  }
}

export function getSaleFormId(
  productId: PBUUID | undefined,
  productsById: Record<string, Product> | undefined
): UUID | undefined {
  if (!productId) {
    return undefined
  }
  const product = productsById?.[UUID.fromPB(productId).toString()]
  return product?.saleFormId && UUID.fromPB(product?.saleFormId)
}

export function hasSaleForm(
  product: Product | undefined,
  listSaleFormsData: ListSaleFormsRes | undefined
): boolean {
  if (!product) {
    return false
  }
  return (
    listSaleFormsData?.saleForms?.some((sf) =>
      UUID.eqFromPB(sf.id, product.saleFormId)
    ) ?? false
  )
}

export function resolveSaleFormCustomFields(
  saleFormId: UUID | undefined,
  editSaleFormFields: SaleForm_CustomField[],
  listSaleFormsData: ListSaleFormsRes | undefined
): SaleForm_CustomField[] {
  if (editSaleFormFields.length > 0) {
    return editSaleFormFields
  }
  return (
    listSaleFormsData?.saleForms?.find(
      (saleForm) => saleFormId && UUID.eqFromPB(saleForm.id, saleFormId)
    )?.customFields ?? []
  )
}

export function requiresNotesModalToOpen(
  product: Product | undefined,
  listSaleFormsData: ListSaleFormsRes | undefined
): boolean {
  return Boolean(
    product?.isTintable ||
      product?.isSpecialOrderableV2 ||
      product?.isNonStock ||
      isTallyProduct(product) ||
      hasSaleForm(product, listSaleFormsData)
  )
}

export type CartLine =
  | {
      productType: typeof CellProductType.PRODUCT
      saleProduct: SaleProductWithOrderInfo
    }
  | { productType: typeof CellProductType.KIT; cartKit: CartKit }

// interleaveByPosition interleaves SaleProducts with SaleKits
export function interleaveByPosition(
  saleProducts: SaleProductWithOrderInfo[],
  cartKits: CartKit[]
): CartLine[] {
  const lines: CartLine[] = [
    ...saleProducts.map((saleProduct) => ({
      productType: CellProductType.PRODUCT,
      saleProduct,
    })),
    ...cartKits.map((cartKit) => ({
      productType: CellProductType.KIT,
      cartKit,
    })),
  ]
  const positionOf = (line: CartLine) =>
    line.productType === CellProductType.PRODUCT
      ? line.saleProduct.position
      : line.cartKit.kit.position
  return lines.sort((a, b) => positionOf(a) - positionOf(b))
}
